import { type UserFileRead } from '@/uppy-file-upload/schemas'
import type { FileUploadState } from '@/uppy-file-upload/store/slice'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
export enum StagedFileStatus {
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  ERROR = 'error',
}

export type StagedFileCardState = {
  fileName: string
  status: StagedFileStatus
  errorMessage: string
  ready: boolean
}

export function isActiveStagedUpload(uploadState?: FileUploadState): uploadState is FileUploadState {
  return !!uploadState && ['validating', 'queued', 'initializing', 'uploading'].includes(uploadState.client.uploadStatus)
}

export function getStagedFileCardState(params: {
  fileId?: string
  file?: UserFileRead
  uploadState?: FileUploadState
  loadError?: boolean
  allowUnknownProcessingStatus?: boolean
}): StagedFileCardState {
  const { fileId, file, uploadState, loadError } = params
  const clientStatus = uploadState?.client.uploadStatus
  const record = uploadState?.backend?.record ?? file
  const fileName = record?.original_filename ?? uploadState?.client.filename ?? fileId ?? 'File'

  if (clientStatus === 'unsupported_type') {
    return { fileName, status: StagedFileStatus.ERROR, errorMessage: 'Unsupported file type', ready: false }
  }

  if (clientStatus === 'error') {
    return { fileName, status: StagedFileStatus.ERROR, errorMessage: uploadState?.client.uploadError ?? 'Upload failed', ready: false }
  }

  if (loadError && !record) {
    return { fileName, status: StagedFileStatus.ERROR, errorMessage: 'Could not load file', ready: false }
  }

  if (isActiveStagedUpload(uploadState)) {
    return { fileName, status: StagedFileStatus.UPLOADING, errorMessage: '', ready: false }
  }

  if (!record) {
    return { fileName, status: StagedFileStatus.PROCESSING, errorMessage: '', ready: false }
  }

  if (record.processing_status === UserFileProcessingStatus.Failed) {
    return { fileName, status: StagedFileStatus.ERROR, errorMessage: record.processing_error ?? 'Processing failed', ready: false }
  }

  if (
    record.processing_status === UserFileProcessingStatus.Pending ||
    record.processing_status === UserFileProcessingStatus.Processing ||
    (!params.allowUnknownProcessingStatus && record.processing_status !== UserFileProcessingStatus.Completed)
  ) {
    return { fileName, status: StagedFileStatus.PROCESSING, errorMessage: '', ready: false }
  }

  return { fileName, status: StagedFileStatus.COMPLETED, errorMessage: '', ready: true }
}
